/**
 * XMLNodeData class
 *
 * @author timduck@cortona3d.com
 */

/**
 * Creates a new data element for the XML element.
 * 
 * @class
 * @classdesc A helper class for representing XML data, for example:
 * ```xml
 * <node att1="value1" att2="value2">
 *      <foo>Foo</foo>
 *      <bar>
 *          <![CDATA[Bar]]>
 *      </bar>
 *      <items>
 *          <item>i1</item>
 *          <item>i2</item>
 *          <item>i3</item>
 *          <item>i4</item>
 *      </items>
 * </node>
 * ```
 * ```
 * {
 *  node: {
 *      "@name": "node",
 *      "@attributes": {
 *          att1: "value1",
 *          att2: "value2"
 *      },
 *      "@children": [
 *          { "@name": "foo", "#text": "Foo" },
 *          { "@name": "bar", "#text": "Bar" },
 *          { "@name": "items", ... }
 *      ],
 *      foo: { "@name": "foo", "#text": "Foo" },
 *      bar: { "@name": "bar", "#text": "Bar" },
 *      items: {
 *          "@name": "items",
 *          "@children": [ ... ],
 *          item: [
 *              { "@name": "item", "#text": "i1" },
 *              { "@name": "item", "#text": "i2" },
 *              { "@name": "item", "#text": "i3" },
 *              { "@name": "item", "#text": "i4" }
 *          ]
 *      }
 *  }
 * }
 * ```
 * @prop {string} #text The text content of a node.
 * @prop {KeyValuePairs} @attributes Node attributes.
 * @prop {XMLNodeData|string[]} @children Node children, including not empty strings.
 */
function XMLNodeData() {}

/**
 * Returns the array of {@link XMLNodeData} objects depending on the argument:
 * 
 * - If type of the argument is the {@link XMLNodeData} object, the method returns an array of one element to the object;
 * - If type of the argument is _array_, the method returns the array itself;
 * - If type of the argument is _string_, the argument value is interpreted as simple XPath expression to select XML elements, If the elements are not found, an empty array is returned;
 * - If argument is omitted, the method returns the array element containing the object itself.
 * 
 * Examples of supported XPath expressions: 
 * - "root/some/path/child" - exact name;
 * - "root//child" - recursive descent; searches for the specified element at any depth;
 * - "root/some/path/*" - wildcard; selects all elements regardless of the element name.
 * 
 * @param {XMLNodeData|string} [obj]
 * @returns {XMLNodeData[]} The array of the {@link XMLNodeData} objects
 */
XMLNodeData.prototype.$ = function (obj, recurs) {
    var current = obj || this;
    var isNotString = function(s) { return typeof s !== 'string'; };
    if (typeof obj === 'string' && obj) {
        if (obj === '*') {
            current = (this['@children'] || []).filter(isNotString);
            if (recurs) {
                current = current.reduce(function (a, node) {
                    return a.concat(node, node.$(obj, recurs));
                }, []);
            }
        } else {
            // nodes path: "root/children/child" --> child = [...]
            // "root//child"
            // "root/*/child" 

            var index = obj.indexOf('/'),
                head = obj.substring(0, index),
                tail = obj.substring(index + 1),
                next = tail.indexOf('/');

            // 'head//some/path'
            if (index >= 0 && next === 0) {
                tail = tail.substring(1);
                current = this.$(head, recurs).filter(isNotString).reduce(function (a, node) {
                    return a.concat(node.$(tail, true));
                }, []);
            } else {
                if (recurs) {
                    current = this.$('*').reduce(function (a, node) {
                        return a.concat(node.$(obj, true));
                    }, this.$(obj));
                } else {
                    current = (index < 0) ? this[obj] : this.$(head).filter(isNotString).reduce(function (a, node) {
                        return a.concat(node.$(tail));
                    }, []);
                }
            }
        }
    }
    return !current ? [] : !current.map ? [current] : current;
};

/**
 * Returns the text content of the element itself or defined by an XPath expression.
 * 
 * @param {string} [path] Simple XPath expression, i.e. "SimulationInteractivity/SimulationInformation/ExporterVersionNumber"
 * @returns {string}
 */
XMLNodeData.prototype.$text = function (path) {
    return !path ? this.toString() : this.$(path).reduce(function (s, node) {
        return s + node;
    }, '');
};

/**
 * Returns the value of a specified attribute on the element.
 * 
 * @param {string} name The name of an attribute
 * @returns {string} Tha value of an attribute
 */
XMLNodeData.prototype.$attr = function (name) {
    if (!this['@attributes']) return null;
    return this['@attributes'][name];
};

/**
 * Returns the text content of the element itself.
 * 
 * @since 2.3.2
 * @returns {string}
 */
XMLNodeData.prototype.toString = function () {
    return this['#text'] || (this['@children'] || []).reduce(function (s, child) {
        return s + child;
    }, '');
};

module.exports = XMLNodeData;